import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { Sparkles, Copy, Save, RefreshCw, Landmark, Building2, ClipboardCheck } from "lucide-react";

const KIND_META = {
  grant_justification: { icon: Landmark, blurb: "Scheme-specific justification for the administering agency — uses your match reasons, eligibility evidence and MRV plan." },
  bank_narrative: { icon: Building2, blurb: "Green loan / SLL narrative for a relationship manager — use of proceeds, case metrics and lender reporting." },
  business_justification: { icon: ClipboardCheck, blurb: "Internal management approval memo — strategic fit, financial case, external funding and risks." },
};

function DraftCard({ iid, kind, label, draft, schemeOptions, onUpdated }) {
  const M = KIND_META[kind];
  const [scheme, setScheme] = useState(draft?.scheme_code || schemeOptions[0]?.code || "");
  const [content, setContent] = useState(draft?.content || "");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setContent(draft?.content || ""); }, [draft?.generated_at, draft?.edited_at]); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try {
      const body = kind === "grant_justification" && scheme ? { scheme_code: scheme } : {};
      const r = await api.post(`/susfin/drafts/${iid}/${kind}`, body);
      onUpdated(r.data);
      toast.success(`${label} drafted`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/susfin/drafts/${iid}/${kind}`, { content });
      onUpdated(r.data);
      toast.success("Draft saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(content); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed — select the text manually"); }
  };

  return (
    <div className="card-flat p-4 space-y-3" data-testid={`sf-draft-${kind}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <M.icon className="h-4 w-4 text-accent mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-semibold text-sm flex items-center gap-2 flex-wrap">
              {label}
              {draft?.edited && <Badge variant="outline" className="text-[8px] uppercase text-[#FFB800] border-[#FFB800]/40">edited</Badge>}
              {draft?.scheme_code && <Badge variant="outline" className="text-[8px] uppercase">{draft.scheme_code}</Badge>}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">{M.blurb}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {kind === "grant_justification" && schemeOptions.length > 0 && (
            <Select value={scheme} onValueChange={setScheme}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid={`sf-draft-scheme-${kind}`}><SelectValue placeholder="Scheme" /></SelectTrigger>
              <SelectContent>
                {schemeOptions.map((s) => <SelectItem key={s.code} value={s.code}>{s.code} ({s.tier})</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant={draft ? "outline" : "primary"} className="h-8" onClick={generate} disabled={busy} data-testid={`sf-draft-generate-${kind}`}>
            {draft ? <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> : <Sparkles className="h-3.5 w-3.5" />}
            {busy ? "Drafting…" : draft ? "Regenerate" : "Generate"}
          </Button>
        </div>
      </div>
      {draft && (
        <>
          <Textarea rows={10} value={content} onChange={(e) => setContent(e.target.value)}
            className="font-mono text-xs leading-relaxed" data-testid={`sf-draft-content-${kind}`} />
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="primary" className="h-8" onClick={save}
              disabled={saving || content === draft.content} data-testid={`sf-draft-save-${kind}`}>
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save edits"}
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={copy} data-testid={`sf-draft-copy-${kind}`}>
              <Copy className="h-3.5 w-3.5" /> Copy
            </Button>
            <span className="text-[9px] text-muted-foreground ml-auto">
              Generated {draft.generated_at?.slice(0, 16).replace("T", " ")} by {draft.generated_by}
              {draft.edited && ` · edited ${draft.edited_at?.slice(0, 16).replace("T", " ")}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default function AiDraftsPanel({ iid, only }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!iid || iid === "null") return;
    setD(null);
    api.get(`/susfin/drafts/${iid}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [iid]);
  if (!iid || iid === "null") return null;
  if (!d) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading AI drafts…</div>;
  const kinds = only ? [only] : Object.keys(d.kinds);
  return (
    <div className="space-y-3" data-testid="sf-drafts-panel">
      {kinds.map((k) => (
        <DraftCard key={k} iid={iid} kind={k} label={d.kinds[k]} draft={d.drafts[k]}
          schemeOptions={d.scheme_options || []} onUpdated={setD} />
      ))}
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}
